import { Block, ContentFilterRef } from '../../../../types/Study';

export interface ResolvedBlockTitle {
  text: string;
  tooltip?: string;
}

export const getRefName = (ref: string | ContentFilterRef) => {
  if (typeof ref === 'string') return ref;
  return ref.name;
};

const getLectureNames = (block: Block): string[] => {
  if (
    block.filterSet?.filterType !== 'custom' ||
    block.filterSet.filters?.scopeType !== 'lecture'
  ) {
    return [];
  }

  return (block.filterSet.filters.lectures ?? [])
    .map(getRefName)
    .filter((name): name is string => Boolean(name?.trim()));
};

/**
 * The default title derived from a block's filter set, ignoring any
 * user-given name. Used as the card title fallback and as the rename
 * modal's placeholder.
 */
export const getDerivedBlockTitle = (block: Block): ResolvedBlockTitle => {
  // Variant-retry blocks are filterType 'session' under the hood, so this
  // must come before the session check.
  if (block.isVariantRetryBlock) {
    return { text: 'Variants Block', tooltip: undefined };
  }

  const lectureNames = getLectureNames(block);
  if (lectureNames.length > 0) {
    const lectureTitle = lectureNames.join(', ');
    return {
      text: lectureTitle,
      tooltip: lectureNames.length > 1 ? lectureTitle : undefined,
    };
  }
  if (block.filterSet?.filterType === 'session') {
    return { text: 'Session Block', tooltip: undefined };
  }
  if (block.filterSet?.filterType === 'topic') {
    const topicName = block.filterSet.topics
      .map(getRefName)
      .find((name) => Boolean(name));

    return { text: topicName ?? 'Topic Block', tooltip: undefined };
  }
  return { text: 'Custom Block', tooltip: undefined };
};

/**
 * Resolves the title shown on a QBank block card.
 *
 * Precedence: explicit `title` prop -> user-given `block.name` -> the
 * default derived from the block's filter set.
 */
export const resolveBlockTitle = (
  block: Block,
  title?: string
): ResolvedBlockTitle => {
  if (title?.trim()) {
    return { text: title, tooltip: undefined };
  }
  if (block.name?.trim()) {
    return { text: block.name, tooltip: undefined };
  }
  return getDerivedBlockTitle(block);
};
